package com.eladfinish.bible.contest.screens.completeVerse.ui

import androidx.compose.foundation.Image
import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.changedToDown
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDirection
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.clear_text
import com.eladfinish.bible.contest.ic_light_bulb_on
import com.eladfinish.bible.contest.ic_trash
import com.eladfinish.bible.contest.ui.inputs.AppTextField
import dev.icerock.moko.resources.compose.localized
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource
import dev.icerock.moko.resources.desc.StringDesc

@Composable
internal fun VerseInputField(
    value: TextFieldValue,
    onValueChange: (TextFieldValue) -> Unit,
    onImeNext: () -> Unit,
    onClear: () -> Unit,
    hint: StringDesc,
    enabled: Boolean,
    textColor: Color,
    focusRequester: FocusRequester,
    showRevealButton: Boolean,
    onRevealPress: () -> Unit,
    onRevealRelease: () -> Unit,
    modifier: Modifier = Modifier,
) {
    AppTextField(
        modifier = modifier,
        value = value,
        onValueChange = onValueChange,
        label = hint.localized(),
        keyboardType = KeyboardType.Text,
        imeAction = ImeAction.Next,
        onImeAction = onImeNext,
        enabled = enabled,
        textStyle = MaterialTheme.typography.bodyLarge.copy(
            fontSize = 22.sp,
            color = textColor,
            textAlign = TextAlign.Start,
            textDirection = TextDirection.Rtl,
        ),
        focusRequester = focusRequester,
        requestFocusOnStart = false,
        leadingIcon = if (showRevealButton) {
            {
                Image(
                    painter = painterResource(SharedRes.images.ic_light_bulb_on),
                    contentDescription = stringResource(SharedRes.strings.clear_text),
                    modifier = Modifier
                        .size(36.dp)
                        .clip(RoundedCornerShape(42.dp))
                        .pointerInput(Unit) {
                            awaitEachGesture {
                                // Use Initial pass — intercepts DOWN/UP before the
                                // OutlinedTextField can consume them.
                                val down = awaitPointerEvent(PointerEventPass.Initial)
                                if (down.changes.none { it.changedToDown() }) return@awaitEachGesture

                                onRevealPress()

                                // Wait until all pointers lift. finally: a rotation ends this gesture
                                // with the finger still down, and the verse must not stay revealed.
                                try {
                                    while (true) {
                                        val up = awaitPointerEvent(PointerEventPass.Initial)
                                        if (up.changes.all { !it.pressed }) break
                                    }
                                } finally {
                                    onRevealRelease()
                                }
                            }
                        },
                    alpha = 0.8f,
                )
            }
        } else null,
        trailingIcon = {
            if (value.text.isNotEmpty()) {
                Image(
                    painter = painterResource(SharedRes.images.ic_trash),
                    contentDescription = stringResource(SharedRes.strings.clear_text),
                    modifier = Modifier
                        .size(36.dp)
                        .clip(RoundedCornerShape(42.dp))
                        .combinedClickable(
                            onClick = onClear,
                            onLongClick = null,
                        ),
                    alpha = 0.6f,
                )
            }
        },
    )
}
